import React, { useState, useRef } from 'react'
import {
  XMarkIcon,
  CloudArrowUpIcon,
  TableCellsIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { materialsApi } from '../../services/adminApi'

interface BatchUploadModalProps {
  onClose: () => void
  onSuccess: () => void
  categories: any[]
  languageOptions: any[]
  levelOptions: any[]
  currentCategoryId?: number
}

interface BatchRow {
  id: string
  data: any
  file?: File
  status: 'pending' | 'processing' | 'success' | 'error'
  error?: string
}

export const BatchUploadModal: React.FC<BatchUploadModalProps> = ({
  onClose,
  onSuccess,
  categories,
  languageOptions,
  levelOptions,
  currentCategoryId
}) => {
  const [rows, setRows] = useState<BatchRow[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [isFinished, setIsFinished] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [currentFileName, setCurrentFileName] = useState<string>('')
  const [csvFileName, setCsvFileName] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 统计信息
  const [stats, setStats] = useState({ success: 0, fail: 0 })

  // 清空并继续上传
  const handleReset = () => {
    setRows([])
    setIsFinished(false)
    setStats({ success: 0, fail: 0 })
    setCurrentFileName('')
    setCsvFileName('')
  }

  // 辅助函数：从路径中提取文件名
  const getFileNameFromPath = (path: string) => {
    if (!path) return ''
    // 同时处理正斜杠和反斜杠
    return path.split(/[/\\]/).pop() || ''
  }

  // 处理拖拽逻辑
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const items = e.dataTransfer.items
    if (!items) return

    let csvFile: File | null = null
    const otherFiles: File[] = []

    // 递归读取所有拖入的文件
    const traverseEntry = async (item: any) => {
      if (item.isFile) {
        const file = await new Promise<File>((resolve) => item.file(resolve))
        if (file.name.toLowerCase().endsWith('.csv')) {
          csvFile = file
        } else if (!file.name.startsWith('.')) {
          otherFiles.push(file)
        }
      } else if (item.isDirectory) {
        const reader = item.createReader()
        const entries: any[] = await new Promise((resolve) => {
          const allEntries: any[] = []
          const readAll = () => {
            reader.readEntries((results: any[]) => {
              if (results.length) {
                allEntries.push(...results)
                readAll()
              } else {
                resolve(allEntries)
              }
            })
          }
          readAll()
        })
        for (const entry of entries) {
          await traverseEntry(entry)
        }
      }
    }

    const traversePromises = []
    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry()
      if (entry) {
        traversePromises.push(traverseEntry(entry))
      }
    }
    await Promise.all(traversePromises)

    if (csvFile) {
      processCSVFile(csvFile, otherFiles)
    } else if (rows.length > 0 && otherFiles.length > 0) {
      // 如果已经有数据行，且拖入了新文件，则进行追加匹配
      matchFilesToExistingRows(otherFiles)
    } else {
      toast.error('请拖入 CSV 文件（可以连同教材文件/文件夹一起拖入以便自动匹配）')
    }
  }

  // 辅助函数：标准化路径
  const normalizePath = (p: string) => p.replace(/\\/g, '/').toLowerCase().trim()

  // 将新拖入的文件与现有行进行匹配
  const matchFilesToExistingRows = (newFiles: File[]) => {
    setRows(prev => {
      let matchCount = 0
      const next = prev.map(row => {
        if (row.file) return row

        const csvPath = normalizePath(row.data.file_url || '')
        if (!csvPath) return row

        // 严格按照路径后缀进行匹配
        const matchedFile = newFiles.find(f => {
          const droppedPath = normalizePath((f as any).webkitRelativePath || f.name)
          return csvPath.endsWith(droppedPath)
        })

        if (matchedFile) {
          matchCount++
          return { ...row, file: matchedFile }
        }
        return row
      })

      if (matchCount > 0) {
        toast.success(`按路径严格匹配成功了 ${matchCount} 个文件`)
      } else {
        toast.error('未找到匹配路径的文件，请确保拖入的文件夹结构与 CSV 一致')
      }
      return next
    })
  }

  // 处理 CSV 文件读取逻辑
  const processCSVFile = (file: File, droppedFiles: File[] = []) => {
    setCsvFileName(file.name)
    const reader = new FileReader()
    reader.onload = (event) => {
      const arrayBuffer = event.target?.result as ArrayBuffer
      let text = ''
      const utf8Decoder = new TextDecoder('utf-8')
      const utf8Text = utf8Decoder.decode(arrayBuffer)
      const hasGarbledChars = utf8Text.includes('\uFFFD') || /[\x80-\xFF]{2,}/.test(utf8Text.slice(0, 500))
      text = hasGarbledChars ? new TextDecoder('gbk').decode(arrayBuffer) : utf8Text

      const parsedRows = parseCSV(text)
      if (parsedRows.length === 0) {
        toast.error('CSV 文件格式不正确')
      } else {
        if (droppedFiles.length > 0) {
          parsedRows.forEach(row => {
            const csvPath = normalizePath(row.data.file_url || '')
            if (csvPath) {
              const matched = droppedFiles.find(f => {
                const droppedPath = normalizePath((f as any).webkitRelativePath || f.name)
                return csvPath.endsWith(droppedPath)
              })
              if (matched) row.file = matched
            }
          })
        }
        setRows(parsedRows)
      }
    }
    reader.readAsArrayBuffer(file)
  }

  // 下载 CSV 模板
  const downloadTemplate = () => {
    const headers = [
      'material_name',
      'material_name_en',
      'description',
      'language',
      'level',
      'category_id',
      'total_pages',
      'file_url',
      'cover_image_url',
      'is_public',
      'relative_path',
      'visibility'
    ]

    // 说明行（用引号包裹避免解析问题）
    const instructions = [
      '"教材名称(必填)"',
      '"英文名称(选填)"',
      '"描述(选填)"',
      '"zh-CN或en"',
      '"beginner|elementary|intermediate|upper-intermediate|advanced"',
      '"分类ID(选填)"',
      '"总页数(数字)"',
      '"文件URL(选填)"',
      '"封面URL(选填)"',
      '"1公开或0私有"',
      '"相对路径-用/分隔多级目录,系统自动创建"',
      '"all所有人|teacher教师|admin管理员"'
    ]

    // 示例数据（展示多级目录结构）
    const examples = [
      // 示例1：三级目录
      [
        '"Power Up 级别0 Unit1"',
        '"Power Up Level0 Unit1"',
        '"剑桥英语初学者教材"',
        'en',
        'beginner',
        '',
        '50',
        '',
        '',
        '1',
        '"剑桥英语-初学者/Power Up 教材/power up 级别0"',
        'all'
      ],
      // 示例2：两级目录
      [
        '"HSK1 第一课"',
        '"HSK1 Lesson 1"',
        '"汉语水平考试教材"',
        'zh-CN',
        'beginner',
        '',
        '30',
        '',
        '',
        '1',
        '"HSK教材/HSK1"',
        'all'
      ],
      // 示例3：单级目录
      [
        '"日常口语100句"',
        '"Daily Conversation 100"',
        '"日常对话练习"',
        'zh-CN',
        'elementary',
        '',
        '20',
        '',
        '',
        '1',
        '"口语练习"',
        'all'
      ],
      // 示例4：音频文件（仅教师可见）
      [
        '"[音频] Unit1 Track01"',
        '"[Audio] Unit1 Track01"',
        '"配套音频资料"',
        'en',
        'beginner',
        '',
        '',
        '',
        '',
        '1',
        '"剑桥英语-初学者/Power Up 教材/Audio"',
        'teacher'
      ]
    ]

    const content = [
      headers.join(','),
      instructions.join(','),
      ...examples.map(ex => ex.join(','))
    ].join('\n')

    const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = '教材批量上传模板_含说明.csv'
    link.click()
  }

  // 解析 CSV（支持带引号、空格、中文的字段）
  const parseCSV = (text: string) => {
    const lines = text.split(/\r?\n/).filter(line => line.trim())
    if (lines.length < 1) return []

    // 解析单行 CSV，正确处理带引号的字段
    const parseCSVLine = (line: string): string[] => {
      const result: string[] = []
      let current = ''
      let inQuotes = false

      for (let i = 0; i < line.length; i++) {
        const char = line[i]
        const nextChar = line[i + 1]

        if (inQuotes) {
          if (char === '"' && nextChar === '"') {
            // 转义的引号
            current += '"'
            i++
          } else if (char === '"') {
            // 结束引号
            inQuotes = false
          } else {
            current += char
          }
        } else {
          if (char === '"') {
            // 开始引号
            inQuotes = true
          } else if (char === ',') {
            // 字段分隔符
            result.push(current.trim())
            current = ''
          } else {
            current += char
          }
        }
      }
      // 添加最后一个字段
      result.push(current.trim())
      return result
    }

    const headers = parseCSVLine(lines[0])
    const results: BatchRow[] = []

    // 检查是否有说明行（包含括号或竖线的行）
    const startLine = lines.length > 1 && (lines[1].includes('(') || lines[1].includes('|') || lines[1].includes('选填') || lines[1].includes('必填')) ? 2 : 1

    for (let i = startLine; i < lines.length; i++) {
      const line = lines[i]
      if (!line.trim()) continue

      const values = parseCSVLine(line)
      const data: any = {}

      headers.forEach((header, index) => {
        data[header] = values[index] || ''
      })

      if (data.material_name) {
        results.push({
          id: Math.random().toString(36).substr(2, 9),
          data,
          status: 'pending'
        })
      }
    }
    return results
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setCsvFileName(file.name)
    const reader = new FileReader()
    reader.onload = (event) => {
      const arrayBuffer = event.target?.result as ArrayBuffer

      // 智能检测编码：先尝试 UTF-8，如果有乱码则用 GBK
      let text = ''

      // 尝试 UTF-8 解码
      const utf8Decoder = new TextDecoder('utf-8')
      const utf8Text = utf8Decoder.decode(arrayBuffer)

      // 检测是否有乱码替换字符 (�) 或常见乱码模式
      const hasGarbledChars = utf8Text.includes('\uFFFD') ||
        /[\x80-\xFF]{2,}/.test(utf8Text.slice(0, 500)) // 检查前500字符是否有高位字节序列

      if (hasGarbledChars) {
        // 尝试 GBK 解码（Excel 中文版默认编码）
        try {
          const gbkDecoder = new TextDecoder('gbk')
          text = gbkDecoder.decode(arrayBuffer)
          console.log('CSV 文件使用 GBK 编码读取')
        } catch {
          // 如果 GBK 解码失败，回退到 UTF-8
          text = utf8Text
          console.log('GBK 解码失败，使用 UTF-8')
        }
      } else {
        text = utf8Text
        console.log('CSV 文件使用 UTF-8 编码读取')
      }

      const parsedRows = parseCSV(text)
      if (parsedRows.length === 0) {
        toast.error('CSV 文件格式不正确或没有数据')
      } else {
        setRows(parsedRows)
      }
    }
    reader.readAsArrayBuffer(file)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const startProcess = async () => {
    const pendingRows = rows.filter(row => row.status !== 'success')
    if (pendingRows.length === 0) {
      toast.error('没有待处理的数据')
      return
    }

    setIsProcessing(true)

    // 使用局部变量跟踪结果，避免 React 状态更新竞态问题
    let successCount = 0
    let failCount = 0

    for (const row of pendingRows) {
      setCurrentFileName(row.data.material_name || (row.file ? row.file.name : ''))
      updateRowStatus(row.id, { status: 'processing' })

      try {
        const { data, file } = row

        let finalFileUrl = data.file_url
        let finalFileSize = data.file_size

        // 如果有实际文件，先上传文件
        if (file) {
          const uploadRes = await materialsApi.uploadFile(file, 'material')
          if (uploadRes.success && uploadRes.data?.url) {
            finalFileUrl = uploadRes.data.url
            finalFileSize = file.size
          } else {
            throw new Error('文件上传失败')
          }
        }

        // 验证必填字段
        if (!data.material_name || !data.language) {
          throw new Error('缺少必填字段 (名称 或 语言)')
        }

        if (!data.category_id && !data.relative_path && !currentCategoryId) {
          throw new Error('缺少分类信息 (分类ID 或 相对路径)')
        }

        const response = await materialsApi.createMaterial({
          material_name: data.material_name,
          material_name_en: data.material_name_en || undefined,
          description: data.description || undefined,
          language: data.language,
          level: data.level || undefined,
          category_id: data.category_id ? parseInt(data.category_id) : (currentCategoryId || undefined),
          relative_path: data.relative_path || undefined,
          total_pages: data.total_pages ? parseInt(data.total_pages) : undefined,
          file_url: finalFileUrl || undefined,
          file_size: finalFileSize || undefined,
          cover_image_url: data.cover_image_url || undefined,
          is_public: data.is_public === '1' || data.is_public === 'true' || data.is_public === true,
          visibility: data.visibility || 'all'
        })

        if (!response.success) {
          throw new Error(response.error?.message || '创建失败')
        }

        updateRowStatus(row.id, { status: 'success' })
        successCount++
      } catch (err: any) {
        updateRowStatus(row.id, { status: 'error', error: err.message || '处理出错' })
        failCount++
      }
    }

    setIsProcessing(false)
    setIsFinished(true)
    setCurrentFileName('')
    setStats({ success: successCount, fail: failCount })

    toast.success(`处理完成：成功 ${successCount} 个，失败 ${failCount} 个`)
    if (successCount > 0) onSuccess()
  }

  const updateRowStatus = (id: string, updates: Partial<BatchRow>) => {
    setRows(prev => prev.map(row => row.id === id ? { ...row, ...updates } : row))
  }

  const removeRow = (id: string) => {
    setRows(prev => prev.filter(row => row.id !== id))
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl max-w-5xl w-full flex flex-col max-h-[90vh] shadow-2xl">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-gray-900">批量上传教材</h3>
            <p className="text-sm text-gray-500 mt-1">请上传包含教材信息的 CSV 文件</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XMarkIcon className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        <div
          className="p-6 flex-1 overflow-y-auto space-y-6"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isDragging && (
            <div className="absolute inset-0 z-50 bg-purple-600 bg-opacity-10 border-4 border-dashed border-purple-600 flex items-center justify-center rounded-xl pointer-events-none">
              <div className="text-center">
                <CloudArrowUpIcon className="w-16 h-16 text-purple-600 mx-auto animate-bounce" />
                <p className="mt-4 text-xl font-bold text-purple-700">将 CSV 文件拖拽到这里</p>
              </div>
            </div>
          )}

          {/* 操作区 */}
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing}
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                <CloudArrowUpIcon className="w-5 h-5 mr-2" />
                选择 CSV 文件
              </button>
              {csvFileName && (
                <div className="flex items-center px-3 py-1.5 bg-purple-50 border border-purple-100 rounded-lg">
                  <TableCellsIcon className="w-4 h-4 text-purple-500 mr-2" />
                  <span className="text-sm font-medium text-purple-700">已加载: {csvFileName}</span>
                </div>
              )}
              <button
                onClick={downloadTemplate}
                className="inline-flex items-center px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                下载模板
              </button>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={handleFileSelect}
              accept=".csv"
            />
          </div>

          {/* 预览表格 */}
          {rows.length > 0 ? (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">状态</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">教材名称</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">语言/级别</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">待匹配文件名</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">分类/路径</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">操作</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rows.map((row) => (
                      <tr key={row.id} className={row.status === 'error' ? 'bg-red-50' : ''}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {row.status === 'pending' && <span className="text-gray-400 text-sm">待处理</span>}
                          {row.status === 'processing' && <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-500 border-t-transparent" />}
                          {row.status === 'success' && <CheckCircleIcon className="w-5 h-5 text-green-500" />}
                          {row.status === 'error' && <ExclamationCircleIcon className="w-5 h-5 text-red-500" />}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 truncate max-w-[200px] font-medium">
                          {row.data.material_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {row.data.language} / {row.data.level}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {row.file ? (
                            <div className="flex flex-col">
                              <span className="text-green-600 font-medium flex items-center">
                                <CheckCircleIcon className="w-3 h-3 mr-1" /> 已匹配本地文件
                              </span>
                              <span className="text-xs text-gray-400 truncate max-w-[150px]">{row.file.name}</span>
                            </div>
                          ) : (
                            <div className="flex flex-col">
                              <span className="text-red-400 text-xs italic">
                                🔍 需匹配: {getFileNameFromPath(row.data.file_url) || '未指定文件'}
                              </span>
                              {!getFileNameFromPath(row.data.file_url) && (
                                <span className="text-[10px] text-gray-300">请检查 CSV 的 file_url 列</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {row.data.category_id ? (
                            <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">
                              ID: {row.data.category_id}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">
                              {row.data.relative_path || '根目录'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {row.status === 'error' ? (
                            <span className="text-red-500 font-medium truncate max-w-[200px] inline-block" title={row.error}>
                              {row.error}
                            </span>
                          ) : (
                            <button
                              onClick={() => removeRow(row.id)}
                              disabled={isProcessing}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <XMarkIcon className="w-5 h-5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-20 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
              <TableCellsIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                请先下载模板，按格式填写后上传或拖拽 CSV 文件到此处
              </p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-between items-center bg-gray-50/50 rounded-b-xl">
          <div className="flex-1 mr-4">
            {isProcessing && currentFileName && (
              <div className="flex items-center text-purple-600 animate-pulse">
                <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
                <span className="text-xs font-bold">
                  正在上传: <span className="ml-1 text-sm underline decoration-purple-300">{currentFileName}</span>
                </span>
              </div>
            )}
            {!isProcessing && (
              <div className="text-xs text-gray-500 italic">
                {isFinished ? (
                  <span className="text-purple-600 font-bold">
                    上传统计：成功 {stats.success} 个，失败 {stats.fail} 个
                  </span>
                ) : (
                  "* 自动创建分类将按照 CSV 中的 relative_path 层级结构进行匹配或新增"
                )}
              </div>
            )}
          </div>
          <div className="flex gap-3">
            {isFinished ? (
              <>
                <button
                  onClick={handleReset}
                  className="px-6 py-2.5 text-sm font-medium text-purple-600 bg-white border border-purple-200 rounded-lg hover:bg-purple-50"
                >
                  清空并继续上传
                </button>
                <button
                  onClick={onClose}
                  className="px-8 py-2.5 text-sm font-bold text-white bg-purple-600 rounded-lg hover:bg-purple-700 shadow-lg shadow-purple-200"
                >
                  完成并关闭
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  disabled={isProcessing}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={startProcess}
                  disabled={isProcessing || rows.length === 0}
                  className="px-8 py-2.5 text-sm font-bold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 shadow-lg shadow-purple-200 transition-all"
                >
                  {isProcessing ? `正在处理 (${rows.filter(r => r.status === 'success').length}/${rows.length})...` : '开始批量上传'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}


